import React, { ReactElement, useEffect, useState, useRef } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { Box, Typography, Grid } from '@mui/material';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import { BaseAppActions } from 'state/baseapplication/Action';
import { LoginSchema } from 'validators/AuthenticationShema';
import { AuthActions } from 'state/authentication/Action';
import { AuthenticationService } from 'services';
import {
    AppFormikTextField,
    AppFormikPasswordField,
    AppPrimaryButton,
    ButtonAAD,
} from 'shared/components/AppComponents';
import { Toast, Utils } from 'shared/helpers';
import {
    AzureAD,
    IAzureADFunctionProps,
    AuthenticationState,
} from 'react-aad-msal';
import { azureAdProps } from 'shared/hooks/AADProvider';
import { SocketActions } from 'state/socket/Action';
import { AxiosError } from 'axios';

/**
 * Login form component using AzureAD for authentication.
 */
export const LoginFormV2 = (props: any): ReactElement => {
    const [azureAuthentication, setAzureAuthentication] = useState<
        'Authenticated' | 'InProgress' | 'Unauthenticated' | null
    >(null);
    const [isLoading, setIsLoading] = useState(false);
    const [captureAzureLogin, setCaptureAzureLogin] = useState(false);
    const [isAzureInitialised, setIsAzureInitialised] = useState(false);
    const isActionCalledRef = useRef(false);

    const {
        SetSocketCloseHandshake,
        setLoginData,
        doLogout,
        setDefaultTheme,
    }: {
        setSocketHandshake: (data: string) => void;
        setLoginData: (data: any) => void;
        doLogout: () => void;
        setDefaultTheme: () => void;
        resetFieldLoggingOut: () => void;
        SetSocketCloseHandshake: () => void;
    } = props;

    React.useLayoutEffect(() => {
        SetSocketCloseHandshake();
    }, []);

    const navigate = useNavigate();

    const loginForm = {
        email: '',
        password: '',
    };

    // API Calls
    const loginPortal = (formValues: any, callback: any): void => {
        AuthenticationService.login({ ...formValues })
            .then((response: any) => {
                callback(true, response);
            })
            .catch((error: any) => {
                callback(false);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    };

    /**
     * handle login AAD using custom token
     *
     * @param {string} accessToken - 'the access token to provide'
     * @param {any} callback - 'callback to complete function'
     */
    const loginAAD = (accessToken: string, callback: any): void => {
        AuthenticationService.loginAAD({
            'x-auth-token': `bearer ${accessToken}`,
        })
            .then((response: any) => {
                callback(response);
            })
            .catch((error: any) => {
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    };
    const [isMSALSubmittion, setMSALSubmittion] = useState<boolean>(false);
    const [isFormikSubmittion, setFormikSubmittion] = useState<boolean>(false);
    /**
     * data login response usage
     *
     * @param {string} response - 'the response get from loginAAD function'
     * @param {any} formik - 'is include formik or not'
     * @param {any} isAAD - 'is Login AAD or not'
     */
    const handleLoginResponse = (
        response: any,
        formik: any,
        isAAD: boolean
    ): void => {
        const {
            tokenId,
            twoFactorEnabled,
            email,
            accessToken,
            refreshToken,
            logo,
            role,
        } = response.data.data;

        if (twoFactorEnabled) {
            navigate('/security-verification', {
                state: {
                    tokenId,
                },
            });
            setFormikSubmittion(false);
            setMSALSubmittion(false);
        } else if (email.verified) {
            setLoginData({
                accessToken,
                refreshToken,
                logo,
                isUserLoggedIn: true,
                isOrgSelected: false,
                role,
                isAAD,
                email,
            });
            setTimeout(() => {
                navigate('/dashboard', { replace: true });
                setFormikSubmittion(false);
                setMSALSubmittion(false);
            }, 500);
        } else {
            navigate('/email-confirmation', {
                replace: true,
                state: {
                    email: email.id,
                },
            });
            setFormikSubmittion(false);
            setMSALSubmittion(false);
            Utils.handleSuccessResponse(response);
        }
    };

    /**
     * on form login page submit
     *
     * @param {any} data - 'data from user'
     * @param {any} formik - 'is inlcude formik value'
     */
    const actionOnFormSubmit = (data: any, formik: any): void => {
        setFormikSubmittion(true);
        if (formik) {
            loginPortal({ ...data }, (status: any, response: any) => {
                formik.setSubmitting(false);
                if (status) {
                    handleLoginResponse(response, formik, false);
                } else {
                    setFormikSubmittion(false);
                    setMSALSubmittion(false);
                }
            });
        } else {
            loginAAD(data, (response: { status: number }) => {
                if (response.status === 200) {
                    handleLoginResponse(response, null, true);
                } else {
                    setFormikSubmittion(false);
                    setMSALSubmittion(false);
                }
            });
        }
    };

    // Hooks
    useEffect(() => {
        doLogout();
        setDefaultTheme();
    }, []);

    // Loader
    useEffect(() => {
        setIsLoading(isLoading);
    }, [isLoading]);

    const AzureADCallback = React.useCallback(
        (props: Partial<IAzureADFunctionProps>): any => {
            setIsAzureInitialised(true);
            const { login, error, authenticationState, accountInfo } = props;

            console.log('---Azure AD error', error);
            /**
             * Checking on submit conditions
             * and loader
             */
            if (
                authenticationState &&
                authenticationState !== azureAuthentication
            ) {
                setAzureAuthentication(authenticationState);
            }
            if (authenticationState !== AuthenticationState.InProgress) {
                setIsLoading(false);
            }

            const authenticated =
                authenticationState === AuthenticationState.Authenticated;

            // Called when status is update after user clicked login
            if (
                authenticated &&
                !isActionCalledRef.current &&
                captureAzureLogin
            ) {
                isActionCalledRef.current = true;
                setMSALSubmittion(true);
                actionOnFormSubmit(accountInfo?.jwtIdToken, false);
            }

            return (
                <Box pt={5}>
                    <ButtonAAD
                        callback={() => {
                            // Called if user is already logged in and has a token
                            if (
                                authenticationState ===
                                    AuthenticationState.Authenticated &&
                                accountInfo?.jwtIdToken
                            ) {
                                setIsLoading(true);
                                isActionCalledRef.current = true;
                                setMSALSubmittion(true);
                                actionOnFormSubmit(
                                    accountInfo?.jwtIdToken,
                                    false
                                );
                            } else if (typeof login === 'function') {
                                // Do fresh login
                                setIsLoading(true);
                                setCaptureAzureLogin(true);
                                login();
                            }
                        }}
                        isLoading={isLoading || isMSALSubmittion}
                    />
                </Box>
            );
        },
        [actionOnFormSubmit, isLoading, isMSALSubmittion]
    );

    const AzureKnot = ({
        login,
        authenticationState,
        accountInfo,
    }: IAzureADFunctionProps): any =>
        // eslint-disable-next-line react/no-unstable-nested-components
        AzureADCallback({ login, authenticationState, accountInfo });

    // UI Elements
    return (
        <Box className="login-form-wrapper">
            <Typography
                data-testid="login-title"
                className="title"
                variant="h2"
            >
                Login
            </Typography>
            {/* <Typography className="subtitle" variant="body1">
                Don’t have an account?
                <Typography
                    to="/sign-up"
                    className="subtitle-link"
                    component={NavLink}
                    variant="body1"
                >
                    Sign Up
                </Typography>
            </Typography> */}

            {process.env.NODE_ENV === 'production' &&
            process.env.REACT_APP_DEV_LOGON !== 'true' ? (
                <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="center"
                    m={10}
                >
                    <img
                        height={275}
                        width={750}
                        src="/images/icons/Logo.svg"
                        alt="UOW Logo"
                        className="mt-5"
                    />
                </Box>
            ) : (
                <>
                    <Box pt={10}>
                        <Formik
                            enableReinitialize
                            initialValues={loginForm}
                            validationSchema={LoginSchema}
                            validateOnMount
                            onSubmit={(formValues, formik) => {
                                setFormikSubmittion(true);
                                actionOnFormSubmit(formValues, formik);
                            }}
                        >
                            {({ handleSubmit, isSubmitting }) => (
                                <form autoComplete="off">
                                    <AppFormikTextField
                                        outerLabel
                                        label="Email *"
                                        name="email"
                                        placeholder="Enter email"
                                        // size="small"
                                    />

                                    <AppFormikPasswordField
                                        outerLabel
                                        label="Password *"
                                        name="password"
                                        placeholder="Enter password"
                                        // size="small"
                                    />

                                    <Box pt={9}>
                                        <Grid container spacing={1}>
                                            <Grid
                                                item
                                                xs={6}
                                                className="start-content"
                                            >
                                                <Typography
                                                    className="forgot-info-text"
                                                    variant="body1"
                                                >
                                                    Did you forget your
                                                    password?
                                                </Typography>
                                                <Typography
                                                    to="/forgot-password"
                                                    className="forgot-subtitle-link"
                                                    component={NavLink}
                                                    variant="body1"
                                                >
                                                    Click here
                                                </Typography>
                                            </Grid>
                                            <Grid
                                                item
                                                xs={6}
                                                className="end-content"
                                            >
                                                <AppPrimaryButton
                                                    fullWidth
                                                    title="Log In"
                                                    isLoading={isSubmitting}
                                                    loadingSize={22}
                                                    loadingText="LOGGING IN"
                                                    disabled={
                                                        isSubmitting ||
                                                        isFormikSubmittion
                                                    }
                                                    onClick={handleSubmit}
                                                    testid="login-submit"
                                                    // className="p-3"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </Box>

                    <Box mt={5} className="login-or-span">
                        <Box className="text-login-span">or</Box>
                    </Box>
                </>
            )}

            <Box pt={5}>
                {/* AAD LOGIN */}
                <Box>
                    <AzureAD {...azureAdProps}>{AzureKnot}</AzureAD>
                    {!isAzureInitialised && (
                        <ButtonAAD
                            loadingText=" "
                            callback={() => {}}
                            disabled
                            isLoading
                        />
                    )}
                </Box>
            </Box>
        </Box>
    );
};

const LoginForm = (props: any): ReactElement => {
    const [isLoading, setIsLoading] = useState(false);
    const [isMSALSubmittion, setMSALSubmittion] = useState<boolean>(false);
    const [isFormikSubmittion, setFormikSubmittion] = useState<boolean>(false);

    const {
        SetSocketCloseHandshake,
        setLoginData,
        doLogout,
        setDefaultTheme,
    }: {
        setSocketHandshake: (data: string) => void;
        setLoginData: (data: any) => void;
        doLogout: () => void;
        setDefaultTheme: () => void;
        resetFieldLoggingOut: () => void;
        SetSocketCloseHandshake: () => void;
    } = props;

    React.useLayoutEffect(() => {
        SetSocketCloseHandshake();
    }, []);

    const navigate = useNavigate();

    const loginForm = {
        email: '',
        password: '',
    };

    // API Calls
    const loginPortal = (formValues: any, callback: any): void => {
        AuthenticationService.login({ ...formValues })
            .then((response: any) => {
                callback(true, response);
            })
            .catch((error: any) => {
                callback(false);
                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    };

    /**
     * handle login AAD using custom token
     *
     * @param {string} accessToken - 'the access token to provide'
     * @param {any} callback - 'callback to complete function'
     */
    const loginAAD = (accessToken: string, callback: any): void => {
        AuthenticationService.loginAAD({
            'x-auth-token': `bearer ${accessToken}`,
        })
            .then((response: any) => {
                callback(response);
            })
            .catch((error: any) => {
                setMSALSubmittion(false);
                setFormikSubmittion(false);

                if (
                    error !== null &&
                    error instanceof AxiosError &&
                    error.code === 'ERR_NETWORK'
                ) {
                    Toast.showErrorMessage(
                        'No internet connection, Please try again!'
                    );
                } else {
                    Utils.handleErrorResponse(error);
                }
            });
    };
    /**
     * data login response usage
     *
     * @param {string} response - 'the response get from loginAAD function'
     * @param {any} formik - 'is include formik or not'
     * @param {any} isAAD - 'is Login AAD or not'
     */
    const handleLoginResponse = (
        response: any,
        formik: any,
        isAAD: boolean
    ): void => {
        const {
            tokenId,
            twoFactorEnabled,
            email,
            accessToken,
            refreshToken,
            logo,
            role,
        } = response.data.data;

        if (twoFactorEnabled) {
            navigate('/security-verification', {
                state: {
                    tokenId,
                },
            });
            setFormikSubmittion(false);
            setMSALSubmittion(false);
        } else if (email.verified) {
            setLoginData({
                accessToken,
                refreshToken,
                logo,
                isUserLoggedIn: true,
                isOrgSelected: false,
                role,
                isAAD,
                email,
            });

            setTimeout(() => {
                const channel = new BroadcastChannel('perpetual-auth-chanel');
                channel.postMessage({
                    type: 'AUTH_STATE',
                    loggedIn: true,
                });
                channel.close();
                navigate('/dashboard', { replace: true });
                setFormikSubmittion(false);
                setMSALSubmittion(false);
            }, 500);
        } else {
            navigate('/email-confirmation', {
                replace: true,
                state: {
                    email: email.id,
                },
            });
            setFormikSubmittion(false);
            setMSALSubmittion(false);
            Utils.handleSuccessResponse(response);
        }
    };

    /**
     * on form login page submit
     *
     * @param {any} data - 'data from user'
     * @param {any} formik - 'is inlcude formik value'
     */
    const actionOnFormSubmit = (data: any, formik: any): void => {
        setFormikSubmittion(true);
        if (formik) {
            loginPortal({ ...data }, (status: any, response: any) => {
                formik.setSubmitting(false);
                if (status) {
                    handleLoginResponse(response, formik, false);
                } else {
                    setFormikSubmittion(false);
                    setMSALSubmittion(false);
                }
            });
        } else {
            loginAAD(data, (response: { status: number }) => {
                if (response.status === 200) {
                    handleLoginResponse(response, null, true);
                } else {
                    setFormikSubmittion(false);
                    setMSALSubmittion(false);
                }
            });
        }
    };

    // Hooks
    useEffect(() => {
        doLogout();
        setDefaultTheme();
        [
            'windowReferences',
            'activeWindow',
            'idleAfter',
            'isTokenRefreshing',
        ].forEach((key) => localStorage.removeItem(key));
    }, []);

    // Loader
    useEffect(() => {
        setIsLoading(isLoading);
    }, [isLoading]);

    // UI Elements
    return (
        <Box className="login-form-wrapper">
            <Typography
                data-testid="login-title"
                className="title"
                variant="h2"
            >
                Login
            </Typography>
            {/* <Typography className="subtitle" variant="body1">
                Don’t have an account?
                <Typography
                    to="/sign-up"
                    className="subtitle-link"
                    component={NavLink}
                    variant="body1"
                >
                    Sign Up
                </Typography>
            </Typography> */}

            <Box pt={10}>
                <Formik
                    enableReinitialize
                    initialValues={loginForm}
                    validationSchema={LoginSchema}
                    validateOnMount
                    onSubmit={(formValues, formik) => {
                        setFormikSubmittion(true);
                        actionOnFormSubmit(formValues, formik);
                    }}
                >
                    {({ handleSubmit, isSubmitting }) => (
                        <form autoComplete="off">
                            <AppFormikTextField
                                outerLabel
                                label="Email *"
                                name="email"
                                placeholder="Enter email"
                                // size="small"
                            />

                            <AppFormikPasswordField
                                outerLabel
                                label="Password *"
                                name="password"
                                placeholder="Enter password"
                                // size="small"
                            />

                            <Box pt={9}>
                                <Grid container spacing={1}>
                                    <Grid item xs={6} className="start-content">
                                        <Typography
                                            className="forgot-info-text"
                                            variant="body1"
                                        >
                                            Did you forget your password?
                                        </Typography>
                                        <Typography
                                            to="/forgot-password"
                                            className="forgot-subtitle-link"
                                            component={NavLink}
                                            variant="body1"
                                        >
                                            Click here
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6} className="end-content">
                                        <AppPrimaryButton
                                            fullWidth
                                            title="Log In"
                                            isLoading={isSubmitting}
                                            loadingSize={22}
                                            loadingText="LOGGING IN"
                                            disabled={
                                                isSubmitting ||
                                                isFormikSubmittion
                                            }
                                            onClick={handleSubmit}
                                            testid="login-submit"
                                            // className="p-3"
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </form>
                    )}
                </Formik>
            </Box>
            {/* 
                    <Box mt={5} className="login-or-span">
                        <Box className="text-login-span">or</Box>
                    </Box> */}

            {false && (
                <Box pt={5}>
                    {/* AAD LOGIN */}
                    <Box>
                        <ButtonAAD
                            loadingText=" "
                            callback={async () => {
                                try {
                                    setMSALSubmittion(true);
                                    setIsLoading(true);
                                    await azureAdProps.provider.login();
                                    const token =
                                        await azureAdProps.provider.getIdToken();
                                    console.log('azure token', token);
                                    actionOnFormSubmit(
                                        token.idToken.rawIdToken,
                                        false
                                    );
                                } catch (error: any) {
                                    setMSALSubmittion(false);
                                    setIsLoading(false);
                                    console.log('Azure Login Error', error);
                                    // Toast.showErrorMessage(error.message);
                                }
                            }}
                            disabled={isMSALSubmittion}
                            isLoading={isMSALSubmittion}
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const mapDispatchToProps = (dispatch: any): any => ({
    setSocketHandshake: (data: string) =>
        dispatch(SocketActions.SetSocketInitialHandshake(data)),
    SetSocketCloseHandshake: () =>
        dispatch(SocketActions.SetSocketCloseHandshake({})),
    setLoginData: (data: any) => dispatch(AuthActions.setLoginStatus(data)),
    setDefaultTheme: (data: any) =>
        dispatch(BaseAppActions.setDefaultTheme(data)),
    doLogout: (data: any) => dispatch(AuthActions.doLogout(data)),
    resetFieldLoggingOut: () => dispatch(AuthActions.resetFieldLoggingOut({})),
});

const mapStateToProps = (state: any): any => ({
    authState: state.authState,
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginForm);
